import { Box, LinearProgress, Typography } from '@mui/material';
import DisplayAlertErrors from '../Form/DisplayAlertErrors';
import EntityPill from '../EntityPill/EntityPill';
import { type TConsentFooterContentProps } from './types';

/**
 * ConsentFooterContent is a purely presentational content block rendered at
 * the bottom of the mobile consent step.
 *
 * Positioning is the responsibility of the parent (e.g. wrapping this in
 * StickyActionBar). This component has no position, z-index, or fixed /
 * sticky styling of its own.
 *
 * Layout (top to bottom):
 *   1. LinearProgress bar (3px, driven by readPct) -- shown only when showProgress is true
 *   2. Row: "Read N%" label (left) | EntityPill (right) -- shown only when showProgress is true
 *   3. DisplayAlertErrors (if errors present)
 *   4. submit node (full-width)
 *   5. back node
 */
export default function ConsentFooterContent({
  readPct,
  entityKind,
  submit,
  back,
  errors,
  showProgress = true,
}: TConsentFooterContentProps) {
  const readPercent = Math.round(readPct * 100);

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
      })}
    >
      {showProgress && (
        <>
          <LinearProgress
            variant="determinate"
            value={readPercent}
            sx={(theme) => ({
              // 3px matches the design spec for the consent progress indicator.
              // No semantic size token maps to 3px in the current token set.
              height: 3,
              backgroundColor: theme.palette.action.disabledBackground,
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main,
              },
            })}
          />

          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: theme.semantic.spacing.sm,
              // Equal vertical padding so the row sits visually centred
              // between the progress bar above and the primary button below.
              py: theme.semantic.spacing.sm,
            })}
          >
            <Typography variant="h6">Read {readPercent}%</Typography>
            <EntityPill kind={entityKind} />
          </Box>
        </>
      )}

      {errors && errors.length > 0 && (
        <Box
          sx={(theme) => ({
            px: theme.semantic.spacing.sm,
          })}
        >
          <DisplayAlertErrors errors={errors} />
        </Box>
      )}

      <Box
        sx={(theme) => ({
          px: theme.semantic.spacing.sm,
          pb: theme.semantic.spacing.sm,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.xs,
        })}
      >
        <Box sx={{ width: '100%' }}>{submit}</Box>
        <Box sx={{ width: '100%' }}>{back}</Box>
      </Box>
    </Box>
  );
}
